import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

// Create the ledger API service
export const ledgerApi = createApi({
  reducerPath: 'ledgerApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Ledger'],
  endpoints: (builder) => ({
    // Get match ledger endpoint
    getMatchLedger: builder.query({
      query: ({ userType, share, page = 1, limit = 50 }) => {
        let url = `/api/match-ledger?page=${page}&limit=${limit}`;
        if (share) {
          url += `&share=${share}`;
        } else if (userType === 'Admin') {
          url += '&share=fixed';
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Ledger'],
    }),
    
    // Get my ledger endpoint
    getMyLedger: builder.query({
      query: ({ userType }) => {
        let url = `/api/admin-ledger`;
        if (userType === 'Admin') {
          url += '?share=fixed';
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Ledger'],
    }),
    
    // Get user type ledger endpoint
    getUserTypeLedger: builder.query({
      query: ({ userType }) => ({
        url: `/api/my-ledger-by-usertype?userType=${userType}`,
        method: 'GET',
      }),
      providesTags: ['Ledger'],
    }),
    
    // Get ledger by user ID endpoint
    getLedgerByUserId: builder.query({
      query: ({ userId, userType }) => ({
        url: `/api/my-ledger-by-userid?userId=${userId}${userType ? `&userType=${userType}` : ''}&filter=group`,
        method: 'GET',
      }),
      providesTags: ['Ledger'],
    }),
    getDownlineLedger: builder.query({
      query: ({ userId, userType }) => ({
        url: `/api/my-ledger-by-userType?userId=${userId}${userType ? `&userType=${userType}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Ledger'],
    }),
    
    // Get deleted transactions by user ID endpoint
    getDeletedTransactionsByUserId: builder.query({
      query: (userId) => ({
        url: `/api/deleted-transactions?userId=${userId}`,
        method: 'GET',
      }),
      providesTags: ['Ledger'],
    }),
    
    // Create cash transaction endpoint
    createCashTransaction: builder.mutation({
      query: (transactionData) => ({
        url: '/api/cash-transaction',
        method: 'POST',
        body: transactionData,
      }),
      invalidatesTags: ['Ledger'],
    }),
    
    // Delete transaction endpoint
    deleteTransaction: builder.mutation({
      query: (transactionId) => ({
        url: `/api/delete-transaction/${transactionId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Ledger'],
    }),
    
    // Get commission ledger endpoint
    getCommissionLedger: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.userId) queryParams.append('userId', params.userId);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.clientId) queryParams.append('clientId', params.clientId);
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);

        const queryString = queryParams.toString();
        return {
          url: `/api/agent/commission-ledger${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Ledger'],
    }),

    // Get commission details endpoint
    getCommissionDetails: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.clientId) queryParams.append('clientId', params.clientId);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        
        const queryString = queryParams.toString();
        return {
          url: `/api/agent/commission-details${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Ledger'],
    }),

    // Reset commission endpoint
    resetCommission: builder.mutation({
      query: (clientId) => ({
        url: '/api/agent/commission-reset',
        method: 'POST',
        body: { clientId },
      }),
      invalidatesTags: ['Ledger'],
    }),

    // Settle a Lena/Dena balance
    createSettlement: builder.mutation({
      query: (payload) => ({
        url: '/api/settlement',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Ledger'],
    }),
  }),
});

// Export hooks for usage in components
export const { 
  
  useGetMatchLedgerQuery, 
  useGetMyLedgerQuery, 
  useGetUserTypeLedgerQuery, 
  useGetLedgerByUserIdQuery,
  useGetDeletedTransactionsByUserIdQuery,
  useCreateCashTransactionMutation,
  useDeleteTransactionMutation,
  useGetDownlineLedgerQuery,
  useGetCommissionLedgerQuery,
  useGetCommissionDetailsQuery,
  useResetCommissionMutation,
  useCreateSettlementMutation
} = ledgerApi;
